import React, { type ReactNode } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';

import { Button, Icon, Input, OptionGroup } from '@column/column-ui-kit';

import { DetailsCard, Notification, RenderFields } from '~/components';
import { Card, Inner, Text } from '~/styles';
import { ReviewTransferRequest, TransferReviewDecision } from '~/typings/enum';

interface Props {
  amount?: string | number;
  bankAccountName: string;
  description?: string;
  counterpartyLabel?: string;
  counterpartyName: string;
  createdByName?: string;
  details?: { label: string; value: ReactNode }[];
  isApprovalDisabled?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  isOpen?: boolean;
  onSubmit: (req: ReviewTransferRequest) => Promise<void>;
}

const Wrapper = styled.div`
  container-type: inline-size;
  container-name: transfer-review;

  padding: 24px var(--page-margin);
`;

const Header = styled.div`
  display: grid;
  grid-column-gap: 24px;
  align-items: start;
  grid-template-columns: auto;
  grid-template-rows: auto;
  grid-row-gap: 8px;

  > *:nth-child(3) {
    grid-column: 1 / -1;
  }

  @container transfer-review (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    grid-template-columns: minmax(180px, 25%) 1fr;
    grid-template-rows: auto auto;
    grid-row-gap: 0px;

    > *:nth-child(3) {
      grid-column: 1 / -1;
    }
  }

  @container transfer-review (min-width: ${({ theme }) => theme.containerBreakpoints.wide}) {
    grid-template-columns: minmax(180px, 25%) auto 25%;
    grid-template-rows: auto;
    grid-row-gap: 0px;

    > *:nth-child(3) {
      grid-column: auto;
    }
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: right;

  padding-left: 24px;
  margin-top: 16px;
  margin-left: auto;
  margin-right: auto;

  @container transfer-review (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    margin-left: 25%;
    margin-right: 0;
  }

  @container transfer-review (min-width: ${({ theme }) => theme.containerBreakpoints.wide}) {
    margin-left: 25%;
    margin-right: 25%;
    padding-right: 24px;
  }
`;

const TransferReviewWrapper = styled.div<{ isOpen?: boolean }>`
  background: ${({ theme }) => theme.body};
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${({ theme }) => theme.secondary.blendToBackground(200)};
  max-height: ${({ isOpen }) => (isOpen ? 'auto' : '0')};
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  overflow: hidden;
  transition:
    max-height 300ms ease-out,
    opacity 300ms ease-out;
`;

const ButtonText = styled.span<{ isActive: boolean }>`
  position: absolute;
  opacity: ${({ isActive }) => (isActive ? 1 : 0)};
  transform: translate(-50%, -50%);
  left: 50%;
  top: 50%;
  text-align: center;
  transition: opacity 0.2s;
`;

const SubmitButton = styled(Button)`
  width: 100px;
  position: relative;
`;

export const TransferReview: React.FC<Props> = ({
  bankAccountName,
  counterpartyLabel = 'Counterparty',
  counterpartyName,
  createdByName,
  description,
  details,
  isApprovalDisabled,
  isDisabled,
  isLoading,
  isOpen,
  onSubmit,
}) => {
  const formMethods = useForm<ReviewTransferRequest>({
    defaultValues: {},
  });
  const decision = formMethods.watch('decision');

  return (
    <TransferReviewWrapper isOpen={isOpen}>
      <Wrapper>
        <Header>
          <Text weight="medium" size={16}>
            Review
          </Text>
          <div>
            {isDisabled && (
              <Inner p={0} pb={18}>
                <Notification color="info" variant="light" withClose={false}>
                  You don't have permission to review this transfer.
                </Notification>
              </Inner>
            )}
            <Inner p={0} pb={isDisabled ? 0 : 16}>
              <DetailsCard
                columns={2}
                details={[
                  { label: 'Account', value: bankAccountName },
                  { label: counterpartyLabel, value: counterpartyName },
                  { label: 'Created By', value: createdByName ?? '-' },
                  { label: 'Description', value: description ?? '-' },
                  ...(details ?? []),
                ]}
              />
            </Inner>
          </div>
        </Header>
        {!isDisabled && (
          <FormProvider {...formMethods}>
            <form autoComplete="off" onSubmit={formMethods.handleSubmit(onSubmit)}>
              {RenderFields<ReviewTransferRequest>({
                sections: [
                  {
                    variant: 'isCompact',
                    fields: [
                      {
                        id: 'decision',
                        label: 'Decision',
                        rules: isDisabled ? undefined : { required: true },
                        children: ({ value, onChange }) => (
                          <Card>
                            <OptionGroup orientation={OptionGroup.Orientation.Horizontal} noBorder>
                              <OptionGroup.Option
                                isActive={value === TransferReviewDecision.Approved}
                                isDisabled={isDisabled || isLoading || isApprovalDisabled}
                                onClick={() => onChange(TransferReviewDecision.Approved)}
                              >
                                <OptionGroup.Option.Radio />
                                <OptionGroup.Option.Label>Approve</OptionGroup.Option.Label>
                                <OptionGroup.Option.Tooltip content="The transfer will be initiated.">
                                  <Icon.CircleInfo />
                                </OptionGroup.Option.Tooltip>
                              </OptionGroup.Option>

                              <OptionGroup.Option
                                isActive={value === TransferReviewDecision.Rejected}
                                isDisabled={isDisabled || isLoading}
                                onClick={() => onChange(TransferReviewDecision.Rejected)}
                              >
                                <OptionGroup.Option.Radio />
                                <OptionGroup.Option.Label>Reject</OptionGroup.Option.Label>
                                <OptionGroup.Option.Tooltip content="The transfer will be marked as rejected, no funds will be withdrawn.">
                                  <Icon.CircleInfo />
                                </OptionGroup.Option.Tooltip>
                              </OptionGroup.Option>
                            </OptionGroup>
                          </Card>
                        ),
                      },
                      {
                        id: 'comment',
                        label: 'Comment',
                        children: ({ onChange, value }, { error }) => {
                          return (
                            <Input
                              isDisabled={isDisabled}
                              onChange={onChange}
                              placeholder={'Comment'}
                              value={value as string | undefined}
                              hasError={!!error}
                            />
                          );
                        },
                      },
                    ],
                  },
                ],
              })}
              <Footer>
                <SubmitButton
                  isDisabled={
                    !decision ||
                    isDisabled ||
                    isLoading ||
                    (decision === TransferReviewDecision.Approved && isApprovalDisabled)
                  }
                  isLoading={isLoading}
                  type={'submit'}
                  variant={decision === 'rejected' ? 'danger' : 'primary'}
                  size={'small'}
                >
                  <ButtonText isActive={!decision || decision === TransferReviewDecision.Approved}>Approve</ButtonText>
                  <ButtonText isActive={decision === TransferReviewDecision.Rejected}>Reject</ButtonText>
                </SubmitButton>
              </Footer>
            </form>
          </FormProvider>
        )}
      </Wrapper>
    </TransferReviewWrapper>
  );
};
